import { yupResolver } from "@hookform/resolvers/yup";
import { AddCircleOutlineRounded } from "@mui/icons-material";
import { FC, useEffect, useMemo, useState } from "react";
import {
  FormProvider,
  useForm,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";

import {
  AccordionHeader,
  AccordionHeaderText,
  BulkInsertFooter,
  FormController,
  InformationModal,
  MuiBox,
  MuiTextField,
  MuiTypography,
  OptionsDataType,
  WarningConfirmationModal,
} from "../../../../../components";

import { useSiteCore } from "../../../../../hooks/useSiteCore";

import {
  useGetBulkParticipantTransferDestinationProgramDetail,
  useGetBulkTransferCompatibleTemplateList,
  useGetNextLanguages,
} from "../../../../../api/services/bulkTransferParticipants/bulkTransferParticipants";
import { useGetCountryListForDropdowns } from "../../../../../api/services/client/clientApis";
import { useGetDeliveryOfficeListQuery } from "../../../../../api/services/mdmOffice/mdmOfficeApis";
import { ButtonWithIcon } from "../../../../../components/buttonWithIcon/ButtonWithIcon";
import { MAX_LENGTH_75 } from "../../../../../helpers/constants";
import {
  getCountryCodeWithoutCountryPrefix,
  isNotEmpty,
  setApiDataToDropDownOption,
} from "../../../../../helpers/utils";
import useAccordion from "../../../../../hooks/useAccordion";
import { useNavigationSiteCore } from "../../../../../hooks/useNavigationSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../../../../redux/slices/appState/appStateSlice";
import {
  bulkTransferSearchInputSelector,
  bulkTransferSelectedParticipantsSelector,
  bulkTransferSelectedStoreParticipantsSelector,
} from "../../../../../redux/slices/program/programSelector";
import {
  updateBulkTransferParticipantDeletion,
  updateBulkTransferStoreParticipantDeletion,
} from "../../../../../redux/slices/program/programSlice";
import { AccordionHeaderRight } from "../../../../../styles/common.style";
import { DestinationProgram } from "../../../../createProgram/modelsAndSchemas/Models";
import { UserCountry } from "../../../../systemMessageSearch/modelsAndSchemas/models";
import {
  CreateBulkTransferFormKey,
  SaveBulkTransferPayload,
  TransferProgramFormKey,
  transferSteps,
  transferTypeKey,
} from "../../../modelsAndSchemas/Models";
import { TransferProgramFormSchema } from "../../../modelsAndSchemas/ValidationSchema";
import { bulkTransferSiteCoreKeys } from "../../../siteCore/SiteCoreKeys";
import { SearchParticipantsResult } from "../bulkTransferSearchParticipants/searchParticipantsResult/SearchParticipantsResult";
import { BulkTransferDetailsWrapper } from "./BulkTransferDetails.style";
import { TransferPrograms } from "./transferPrograms/TransferPrograms";

interface BulkActivityDetailsProps {
  pageId: string;
  handleBackButtonClick: () => void;
  handleNavigateBack: () => void;
  onConfirmBtnClick: (data: SaveBulkTransferPayload) => void;
  currentStep?: number;
  selectedTransferType: string;
}

const initialAccordionStatus = {
  participants: false,
  all: false,
  transfer: true,
};

export interface EditDeleteState {
  id: string | number;
  isEdit: boolean;
  isDelete: boolean;
  entityType: "activity" | "participant" | undefined;
}

export const BulkTransferDetails: FC<BulkActivityDetailsProps> = ({
  pageId,
  handleBackButtonClick,
  handleNavigateBack,
  onConfirmBtnClick,
  selectedTransferType,
  currentStep,
}) => {
  const { accordionStatus, handleIndividualAccordionChange } = useAccordion(
    initialAccordionStatus,
  );
  const sourceProgramDetails = useSelector(bulkTransferSearchInputSelector);

  const [deleteParticipantId, setDeleteParticipantId] = useState<string>("");

  const [showInformationModal, setShowInformationModal] = useState(false);

  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const [compatibleTemplateList, setCompatibleTemplateList] = useState<
    OptionsDataType[]
  >([]);

  const [destinationProgramDetails, setDestinationProgramDetails] =
    useState<DestinationProgram>({} as DestinationProgram);

  const [isDestinationError, setIsDestinationError] = useState<boolean>(false);

  const dispatch = useDispatch();
  const { control: formControlMain } = useFormContext();

  const {
    commonLabels,
    fetchLabel,
    commonValidationMessages,
    fetchComponentTitle,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();

  const filteredParticipants = useSelector(
    bulkTransferSelectedParticipantsSelector,
  );

  const storedParticipants = useSelector(
    bulkTransferSelectedStoreParticipantsSelector,
  );

  const {
    data: countryListData,
    isError: isCountryError,
    isFetching: isCountryFetching,
  } = useGetCountryListForDropdowns();

  const {
    data: compatibleProgramTemplateList,
    isPending: isCompatibleProgramTemplateLoading,
    mutateAsync: getCompatibleProgramTemplates,
  } = useGetBulkTransferCompatibleTemplateList();

  const {
    data: destinationProgramData,
    isError: isDestinationProgramDetailError,
    isPending: isDestinationProgramDetailLoading,
    mutateAsync: getDestinationProgramDetail,
  } = useGetBulkParticipantTransferDestinationProgramDetail();

  const {
    data: deliveryCountryList,
    refetchWithParams: refetchDeliveryCountryList,
  } = useGetDeliveryOfficeListQuery();

  const {
    isFetching: isNextLanguagePending,
    data: nextLanguageData,
    isError: isNextLanguageError,
    refetchWithParams: refetchNextLanguages,
  } = useGetNextLanguages();

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible:
          isCountryFetching ||
          isCompatibleProgramTemplateLoading ||
          isDestinationProgramDetailLoading ||
          isNextLanguagePending,
      }),
    );
    return () => {
      dispatch(
        toggleLoader({
          isVisible: false,
        }),
      );
    };
  }, [
    isCountryFetching,
    isCompatibleProgramTemplateLoading,
    isDestinationProgramDetailLoading,
    isNextLanguagePending,
  ]);

  useEffect(() => {
    if (isCountryError || isNextLanguageError) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
        }),
      );
    }
  }, [isCountryError, isNextLanguageError]);

  const selectedParticipants = useMemo(
    () => filteredParticipants?.filter((participant) => participant.isSelected),
    [filteredParticipants],
  );

  const getSelectedParticipantLabel = (key: string) => {
    return fetchLabel(
      bulkTransferSiteCoreKeys.searchParticipantsResultsKey,
      key,
    );
  };

  const handleDeleteClick = () => {
    if (deleteParticipantId) {
      dispatch(
        updateBulkTransferParticipantDeletion({
          id: deleteParticipantId as string,
        }),
      );
      dispatch(
        updateBulkTransferStoreParticipantDeletion({
          id: deleteParticipantId as string,
        }),
      );
      dispatch(
        toggleToast({
          open: true,
          message: getSelectedParticipantLabel(
            bulkTransferSiteCoreKeys.participantDeletedToastMessage,
          ),
        }),
      );

      handleModalClose();
    }
  };

  const handleModalClose = () => {
    if (showDeleteConfirmModal) {
      setShowDeleteConfirmModal(false);
      setDeleteParticipantId("");
    }
  };

  const handleDeleteParticipant = (id: string) => {
    if (selectedParticipants.length <= 2) {
      setShowInformationModal(true);
    } else {
      setDeleteParticipantId(id);
      setShowDeleteConfirmModal(true);
    }
  };

  const renderHeader = (title: string, selectedCount: number) => {
    return (
      <MuiBox className="form-header">
        <MuiBox className="form-header-left">
          <MuiBox className="title-container">
            <MuiTypography
              variant="h2SemiBold"
              className="form-header-left"
              component="h2"
            >
              {title}
            </MuiTypography>
            {selectedCount > 0 && (
              <MuiTypography variant="h3Bold" className="selected-count">
                {selectedCount}
              </MuiTypography>
            )}
          </MuiBox>
        </MuiBox>
      </MuiBox>
    );
  };

  const handleCloseInformation = () => {
    setShowInformationModal(false);
  };

  const renderHeaderTransfer = (labelKey: string) => {
    return (
      <AccordionHeaderText
        label={fetchLabel(
          bulkTransferSiteCoreKeys.programsAndCandidatesTransferDetailsKey,
          labelKey,
        )}
      />
    );
  };

  const defaultTransferValues = {
    [TransferProgramFormKey.compatibleProgramTemplate]: "",
    [TransferProgramFormKey.destinationCountry]: "",
    [TransferProgramFormKey.deliveryOffice]: "",
    [TransferProgramFormKey.nextLanguage]: "",
  };

  const methods = useForm({
    mode: "all",
    defaultValues: defaultTransferValues,
    resolver: yupResolver(
      TransferProgramFormSchema(commonValidationMessages, selectedTransferType),
    ),
  });

  const { handleSubmit, resetField, reset, watch, getValues, control } =
    methods;

  useEffect(() => {
    reset(defaultTransferValues);
    setDestinationProgramDetails({} as DestinationProgram);
    setIsDestinationError(false);
  }, [selectedTransferType]);

  const programIds = () =>
    selectedParticipants.map((participant) => participant.programId);

  const storedProgramIds = useMemo(
    () =>
      storedParticipants
        ?.filter((participant) => participant.isSelected)
        .map((participant) => participant.programId),
    [storedParticipants],
  );

  useEffect(() => {
    if (
      selectedTransferType === transferTypeKey.changeProgram &&
      currentStep === transferSteps.transferDetails
    ) {
      resetField(TransferProgramFormKey.compatibleProgramTemplate);
      if (selectedParticipants.length) {
        getCompatibleProgramTemplates({
          programIds: programIds(),
        });
      } else {
        setCompatibleTemplateList([]);
      }
    } else if (
      selectedTransferType === transferTypeKey.changeCountry &&
      currentStep === transferSteps.transferDetails
    ) {
      resetField(TransferProgramFormKey.deliveryOffice);
      resetField(TransferProgramFormKey.destinationCountry);
      setDestinationProgramDetails({} as DestinationProgram);
      setIsDestinationError(false);
    }
  }, [JSON.stringify(storedProgramIds)]);

  useEffect(() => {
    if (compatibleProgramTemplateList) {
      setCompatibleTemplateList(
        setApiDataToDropDownOption(compatibleProgramTemplateList?.data || []),
      );
    }
  }, [compatibleProgramTemplateList]);

  useEffect(() => {
    const { compatibleProgramTemplate } = getValues();
    if (compatibleProgramTemplate) {
      getDestinationProgramDetail({
        programIds: programIds(),
        programTemplateId: compatibleProgramTemplate,
      });
    } else {
      setDestinationProgramDetails({} as DestinationProgram);
      setIsDestinationError(false);
    }
  }, [watch(TransferProgramFormKey.compatibleProgramTemplate)]);

  useEffect(() => {
    const { deliveryOffice, destinationCountry } = getValues();
    if (destinationCountry && deliveryOffice) {
      getDestinationProgramDetail({
        programIds: programIds(),
        countryCode: destinationCountry,
        deliveryOfficeId: deliveryOffice,
      });
    } else {
      setDestinationProgramDetails({} as DestinationProgram);
      setIsDestinationError(false);
    }
  }, [
    watch(TransferProgramFormKey.deliveryOffice),
    watch(TransferProgramFormKey.destinationCountry),
  ]);

  useEffect(() => {
    if (isNotEmpty(destinationProgramData?.data)) {
      setDestinationProgramDetails(destinationProgramData?.data);
    }
  }, [destinationProgramData]);

  const onSubmit = () => {
    handleSubmit(() => setShowConfirmationModal(true))();
  };

  const onConfirm = () => {
    const {
      compatibleProgramTemplate,
      deliveryOffice,
      destinationCountry,
      nextLanguage,
    } = getValues();
    setShowConfirmationModal(false);
    onConfirmBtnClick({
      programTemplateId: compatibleProgramTemplate,
      deliveryOfficeId: deliveryOffice ?? "",
      countryCode: destinationCountry ?? "",
      languageCode: nextLanguage ?? "",
    });
  };

  useEffect(() => {
    setIsDestinationError(isDestinationProgramDetailError);
  }, [isDestinationProgramDetailError]);

  const destinationCountry = useWatch({
    control: control,
    name: TransferProgramFormKey.destinationCountry,
  });

  useEffect(() => {
    if (destinationCountry) {
      resetField(TransferProgramFormKey.deliveryOffice);
      refetchDeliveryCountryList(
        getCountryCodeWithoutCountryPrefix(destinationCountry).toUpperCase(),
      );
      refetchNextLanguages(destinationCountry);
    }
  }, [destinationCountry]);

  const nextLanguageOptions = useMemo(() => {
    let nextLanguages: OptionsDataType[] = [];
    if (isNotEmpty(nextLanguageData)) {
      nextLanguages = nextLanguageData.map((country: UserCountry) => ({
        label: country.localeDescription,
        value: country.localeName,
      }));
    }
    return nextLanguages;
  }, [nextLanguageData]);

  return (
    <BulkTransferDetailsWrapper data-testid="bulk-transfer-details">
      <AccordionHeader
        wrapperClassName="activity-details-accordion"
        title={renderHeader(
          getSelectedParticipantLabel(
            bulkTransferSiteCoreKeys.selectedParticipantsHeader,
          ),
          selectedParticipants?.length ?? 0,
        )}
        onChange={() => handleIndividualAccordionChange("participants")}
        isExpanded={accordionStatus.participants}
      >
        <AccordionHeaderRight className="with-custom-mobile-padding">
          <ButtonWithIcon
            ActionIcon={AddCircleOutlineRounded}
            data-testid={`${pageId}-add-participants`}
            buttonText={getSelectedParticipantLabel(
              bulkTransferSiteCoreKeys.addParticipantsLink,
            )}
            onActionClick={handleBackButtonClick}
          />
        </AccordionHeaderRight>
        <SearchParticipantsResult
          rows={selectedParticipants ?? []}
          pageId={pageId}
          currentStep={1}
          handleDeleteClick={handleDeleteParticipant}
        />
      </AccordionHeader>

      <MuiBox
        className="accordion-header"
        data-testid="bulk-transfer-process-details"
      >
        <AccordionHeader
          title={
            <AccordionHeaderText
              label={fetchComponentTitle(
                bulkTransferSiteCoreKeys.bulkProcessDetails,
              )}
            />
          }
          isExpanded
        >
          <MuiBox className="name-field">
            <FormController
              key={CreateBulkTransferFormKey.bulkProcessName}
              control={formControlMain}
              name={CreateBulkTransferFormKey.bulkProcessName}
            >
              <MuiTextField
                id={CreateBulkTransferFormKey.bulkProcessName}
                placeholder={fetchLabel(
                  bulkTransferSiteCoreKeys.bulkProcessDetails,
                  bulkTransferSiteCoreKeys.bulkProcessNamePlaceholderText,
                )}
                maxLength={MAX_LENGTH_75}
                shouldTrimSpaces={false}
                isMandatory
                label={fetchLabel(
                  bulkTransferSiteCoreKeys.bulkProcessDetails,
                  bulkTransferSiteCoreKeys.bulkProcessName,
                )}
                requiredARIALabel={commonARIALabel.required}
                tooltipMessage={
                  commonValidationMessages.fieldSpecialCharactersTooltipMessage
                }
                tooltipHeaderTitle={
                  commonValidationMessages.allowedSpecialCharactersTitle
                }
              />
            </FormController>
          </MuiBox>
        </AccordionHeader>
      </MuiBox>

      <AccordionHeader
        wrapperClassName="activity-details-accordion"
        title={renderHeaderTransfer(
          selectedTransferType === transferTypeKey.changeProgram
            ? bulkTransferSiteCoreKeys.programTransfer
            : bulkTransferSiteCoreKeys.countryTransfer,
        )}
        onChange={() => handleIndividualAccordionChange("transfer")}
        isExpanded={accordionStatus.transfer}
      >
        <FormProvider {...methods}>
          <TransferPrograms
            pageId={pageId}
            selectedTransferType={selectedTransferType}
            countryListData={setApiDataToDropDownOption(
              countryListData,
              true,
              sourceProgramDetails?.deliveryCountryName,
            )}
            deliveryOfficeList={setApiDataToDropDownOption(deliveryCountryList)}
            compatibleProgramTemplateList={compatibleTemplateList}
            isNoDestinationTemplate={isDestinationError}
            destinationProgramData={
              destinationProgramDetails as DestinationProgram
            }
            nextLanguageData={nextLanguageOptions}
          />
        </FormProvider>
      </AccordionHeader>

      {showDeleteConfirmModal && (
        <WarningConfirmationModal
          show={showDeleteConfirmModal}
          onClose={handleModalClose}
          modalTitle={commonLabels.confirmation}
          onPrimaryButtonClick={handleDeleteClick}
          labelContent={getSelectedParticipantLabel(
            bulkTransferSiteCoreKeys.deleteParticipantConfirmation,
          )}
          secondaryButtonTitle={commonLabels.no}
        />
      )}

      <BulkInsertFooter
        pageId={pageId}
        onConfirmClick={onSubmit}
        handleNavigateBack={handleNavigateBack}
        isFiledUpdated={true}
        confirmButtonText={commonLabels.confirm}
        showBackButton={true}
        handleBackButtonClick={handleBackButtonClick}
        disableConfirmButton={isDestinationError}
      />

      {showInformationModal && (
        <InformationModal
          showModal={showInformationModal}
          handleModalClose={handleCloseInformation}
          handlePrimaryButtonClick={handleCloseInformation}
          title={commonLabels.warning}
          message={getSelectedParticipantLabel(
            bulkTransferSiteCoreKeys.minimumTwoParticipantsRequired,
          )}
        />
      )}
      {showConfirmationModal && (
        <WarningConfirmationModal
          modalTitle={commonLabels.confirmation}
          isLabelHTMLContent={true}
          labelContent={fetchLabel(
            bulkTransferSiteCoreKeys.programsAndCandidatesTransferDetailsKey,
            bulkTransferSiteCoreKeys.bulkTransferConfirmationMessage,
          )}
          show={showConfirmationModal}
          onClose={() => setShowConfirmationModal(false)}
          onPrimaryButtonClick={onConfirm}
          primaryButtonTitle={commonLabels.yesProceed}
          secondaryButtonTitle={commonLabels.no}
        />
      )}
    </BulkTransferDetailsWrapper>
  );
};
